// src/components/ProductShowcase.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// 🖼️ Import your local images (add/replace as needed)
import wb500bc6 from "./images/wb500bc6.png";
import wb440bc8 from "./images/wb440bc8.png";
import wb500bc9 from "./images/wb500bc9.png";
import wb375bc12 from "./images/wb375bc12.png";
import wb375bc15 from "./images/wb375bc15.png";

type Slide = {
  name: string;
  style: string;
  abv: string;
  availability: string;
  img: string;   // imported path
  bg: string;    // hex color e.g. "#6B4FD1"
};

const slides: Slide[] = [
  {
    name: "Kentucky Bourbon & Cola",
    style: "500ml",
    abv: "6%",
    availability: "Nationwide",
    img: wb500bc6,
    bg: "#C73133", 
  },
  {
    name: "Kentucky Bourbon & Cola",
    style: "440ml",
    abv: "8%",
    availability: "Seasonal",
    img: wb440bc8,
    bg: "#0C2340", 
  },
  {
    name: "Kentucky Bourbon & Cola",
    style: "500ml",
    abv: "9%",
    availability: "Nationwide",
    img: wb500bc9,
    bg: "#004AAD", 
  },
  {
    name: "Kentucky Bourbon & Cola",
    style: "375ml",
    abv: "12%",
    availability: "Seasonal",
    img: wb375bc12,
    bg: "#7ED957", 
  },
  {
    name: "Kentucky Bourbon & Cola",
    style: "375ml",
    abv: "15%",
    availability: "Seasonal",
    img: wb375bc15,
    bg: "#393536", 
  },
];

export default function ProductShowcase() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState<1 | -1>(1);
  const touchStartX = useRef<number | null>(null);

  const len = slides.length;
  const prevIndex = (index - 1 + len) % len;
  const nextIndex = (index + 1) % len;

  const goTo = useCallback((i: number, d: 1 | -1) => {
    setDir(d);
    setIndex((i + len) % len);
  }, [len]);

  const next = useCallback(() => goTo(index + 1, 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1, -1), [index, goTo]);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  // Touch
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) (delta < 0 ? next : prev)();
    touchStartX.current = null;
  };

  const variants = {
    enter: (direction: 1 | -1) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
      position: "absolute" as const,
    }),
    center: {
      x: 0,
      opacity: 1,
      position: "relative" as const,
    },
    exit: (direction: 1 | -1) => ({
      x: direction > 0 ? "-100%" : "100%",
      opacity: 0,
      position: "absolute" as const,
    }),
  };

  const current = slides[index];

  return (
    <section
      className="relative w-full min-h-[90vh] md:min-h-[100vh] overflow-hidden"
      style={{ backgroundColor: current.bg }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Side faded previews */}
      <SidePreview
        side="left"
        img={slides[prevIndex].img}
        alt={`Prev ${slides[prevIndex].name}`}
      />
      <SidePreview
        side="right"
        img={slides[nextIndex].img}
        alt={`Next ${slides[nextIndex].name}`}
      />

      {/* Slide counter */}
      <div className="absolute top-8 right-6 md:top-12 md:right-12 z-20 text-white/95 font-heading">
        <span className="text-5xl md:text-7xl leading-none">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="text-xl md:text-2xl align-top opacity-80">/{String(len).padStart(2, "0")}</span>
      </div>

      {/* Main slide content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-20 h-full">
        <AnimatePresence initial={false} custom={dir} mode="popLayout">
          <motion.div
            key={index}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "tween", duration: 0.55 }}
            className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-x-1 md:gap-x-1 lg:gap-x-1"
          >
            {/* Left: Big name */}
            <div className="order-2 md:order-1 text-left md:text-right">
              <h2
                className="text-white drop-shadow-xl font-heading font-bold leading-tight
                          text-3xl sm:text-4xl md:text-5xl lg:text-7xl"
              >
                {breakLines(current.name)}
              </h2>
            </div>

            {/* Center: Main product image */}
            <div className="order-1 md:order-2 flex justify-center md:justify-center">
              <img
                src={current.img}
                alt={current.name}
                className="object-contain drop-shadow-2xl
                          w-[55%] max-w-[420px] md:w-[70%] md:max-w-[460px] lg:max-w-[500px]"
              />
            </div>

            {/* Right: Details */}
            <div className="order-3 md:order-3 text-white text-left md:text-left">
              <Detail label="SIZE" value={current.style} />
              <hr className="my-4 border-white/30" />
              <Detail label="ABV" value={current.abv} strong />
              <hr className="my-4 border-white/30" />
              <Detail label="AVAILABILITY" value={current.availability} strong />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>


      {/* Arrows */}
      <button
        aria-label="Previous"
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30
                   text-white/90 hover:text-white text-5xl md:text-6xl select-none"
        style={{ textShadow: "0 2px 8px rgba(0,0,0,0.55)" }}
      >
        &lt;
      </button>
      <button
        aria-label="Next"
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30
                   text-white/90 hover:text-white text-5xl md:text-6xl select-none"
        style={{ textShadow: "0 2px 8px rgba(0,0,0,0.55)" }}
      >
        &gt;
      </button>
    </section>
  );
}

/** Faded side peeking images */
function SidePreview({ side, img, alt }: { side: "left" | "right"; img: string; alt: string }) {
  const base =
    "hidden md:block absolute top-1/2 -translate-y-1/2 z-0 opacity-40 pointer-events-none";
  // push most of the image off-screen; only ~60–80px peeks in
  const sidePos =
    side === "left"
      ? "-left-[290px] sm:-left-[250px] md:-left-[230px]"
      : "-right-[290px] sm:-right-[250px] md:-right-[230px]";

  return (
    <img
      src={img}
      alt={alt}
      className={`${base} ${sidePos}`}
      style={{
        width: "360px",           // large image so the visible sliver looks natural
        maxWidth: "32vw",
        objectFit: "contain",
        filter: "drop-shadow(0 10px 24px rgba(0,0,0,0.35))",
      }}
    />
  );
}


/** Detail block */
function Detail({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <div className="text-white/80 tracking-wide text-sm mb-2">{label}</div>
      <div className={`whitespace-pre-line ${strong ? "font-extrabold" : "font-semibold"} text-2xl md:text-3xl`}>
        {value}
      </div>
    </div>
  );
}

/** Utility: convert "A\nB" strings to <br/> breaks if needed elsewhere */
function breakLines(text: string) {
  return text.split("\n").map((t, i) => (
    <span key={i}>
      {t}
      {i < text.split("\n").length - 1 && <br />}
    </span>
  ));
}
